import { COLLECTION_TYPES } from '@/core/collections/constants';
import * as jsonpatch from 'fast-json-patch';
import Token from './model';
import { createGUID } from '@/table/utils/guid';
import { buildSpawnToken } from '@/characters/utils/spawnTokenData';
import { sendUpdate } from '@/core/collections/write';

// TODO: type this — Vuex module context and token object types
/* eslint-disable @typescript-eslint/no-explicit-any */
type Ctx = any;
/* eslint-enable @typescript-eslint/no-explicit-any */

interface TokenState {
    selectedToken: Record<string, unknown>;
}

function getTokensForMap(rootState: Ctx, mapId: string): Record<string, unknown> {
    return (rootState.collections[COLLECTION_TYPES.maps]?.[mapId]?.tokens ?? {}) as Record<string, unknown>;
}

function findTokenInMap(rootState: Ctx, id: string, mapId: string): Record<string, unknown> | null {
    if (!mapId) {
        return null;
    }
    return (getTokensForMap(rootState, mapId)[id] as Record<string, unknown>) ?? null;
}

function prefixTokenPatch(patch: jsonpatch.Operation[], tokenId: string): jsonpatch.Operation[] {
    return patch.map((op) => ({ ...op, path: `/tokens/${tokenId}${op.path}` }));
}

export const getters = {
    getRawToken: (_state: Ctx, _getters: Ctx, rootState: Ctx) => (id: string, mapId: string) => {
        const token = findTokenInMap(rootState, id, mapId);
        if (!token) return null;
        return { ...token, _id: id, mapId };
    },
    getToken: (_state: Ctx, getters: Ctx) => (id: string, mapId: string) => {
        return new Token(getters.getRawToken(id, mapId));
    },
    getTokens: (_state: Ctx, _getters: Ctx, rootState: Ctx) => {
        const maps = rootState.collections[COLLECTION_TYPES.maps] ?? {};
        return (Object.values(maps) as Record<string, unknown>[]).flatMap((map) => {
            const tokens = map.tokens as Record<string, unknown> | undefined;
            if (!tokens) return [];
            return Object.entries(tokens).map(
                ([id, token]) => new Token({ ...(token as Record<string, unknown>), _id: id, mapId: map._id }),
            );
        });
    },
    getTokensByMapId: (_state: Ctx, _getters: Ctx, rootState: Ctx) => (mapId: string) => {
        const tokens = getTokensForMap(rootState, mapId);
        return Object.entries(tokens).map(
            ([id, token]) => new Token({ ...(token as Record<string, unknown>), _id: id, mapId }),
        );
    },
};

export const mutations = {
    updateSelectedToken(state: TokenState, token: Record<string, unknown>): void {
        state.selectedToken = token;
    },
};

export const actions = {
    async spawn({ dispatch, rootState }: Ctx, token: Record<string, unknown>): Promise<unknown> {
        const id = createGUID();
        // Where the token was dropped belongs to the token, not to the character it carries.
        const { mapId, pos, ...character } = token;
        const newToken = new Token({
            ...buildSpawnToken(character),
            pos,
            _userid: rootState.profile.me.id,
            _campaign: rootState.live.sessionId,
        });
        return await dispatch(
            'collections/update',
            {
                collection: COLLECTION_TYPES.maps,
                id: mapId,
                patch: [{ op: 'add', path: `/tokens/${id}`, value: newToken }],
            },
            { root: true },
        );
    },

    async remove({ getters, dispatch }: Ctx, { id, mapId }: { id: string; mapId: string }): Promise<unknown> {
        if (!id) return;
        if (!getters.getRawToken(id, mapId)) return;
        return await dispatch(
            'collections/update',
            {
                collection: COLLECTION_TYPES.maps,
                id: mapId,
                patch: [{ op: 'remove', path: `/tokens/${id}` }],
            },
            { root: true },
        );
    },

    async restore({ dispatch }: Ctx, token: Record<string, unknown>): Promise<unknown> {
        if (!token?._id || !token?.mapId) return;
        const { _id, mapId, ...value } = token;
        return await dispatch(
            'collections/update',
            {
                collection: COLLECTION_TYPES.maps,
                id: mapId,
                patch: [{ op: 'add', path: `/tokens/${_id}`, value }],
            },
            { root: true },
        );
    },

    async update({ getters, dispatch }: Ctx, editedToken: Record<string, unknown>): Promise<unknown> {
        const currentRaw = getters.getRawToken(editedToken._id as string, editedToken.mapId as string);
        if (!currentRaw) throw new Error(`Could not find Token to update with id '${editedToken._id}'`);

        const changes = { ...editedToken, pos: currentRaw.pos };
        const tokenPatch = jsonpatch
            .compare(currentRaw, new Token(changes))
            .filter((op) => !op.path.startsWith('/_id') && !op.path.startsWith('/mapId'));
        if (!tokenPatch.length) return null;

        return await sendUpdate(dispatch, {
            collection: COLLECTION_TYPES.maps,
            id: currentRaw.mapId as string,
            patch: prefixTokenPatch(tokenPatch, editedToken._id as string),
        });
    },

    async moveToken({ getters, dispatch }: Ctx, movedToken: Record<string, unknown>): Promise<unknown> {
        const mapId = movedToken.mapId as string;
        const currentRaw = getters.getRawToken(movedToken._id as string, mapId);
        if (!currentRaw) throw new Error(`Could not find Token to move with id '${movedToken._id}'`);

        const posPatch = jsonpatch.compare({ pos: currentRaw.pos }, { pos: movedToken.pos });
        if (!posPatch.length) return null;

        return await dispatch(
            'collections/update',
            {
                collection: COLLECTION_TYPES.maps,
                id: mapId,
                patch: prefixTokenPatch(posPatch, movedToken._id as string),
            },
            { root: true },
        );
    },
};

const TokenStore = {
    namespaced: true,
    state: {
        selectedToken: {} as Record<string, unknown>,
    },
    getters,
    mutations,
    actions,
};

export default TokenStore;
